import React, { useState } from 'react';
import { format } from 'date-fns';
import {
  XMarkIcon,
  ClockIcon,
  RocketLaunchIcon,
  TruckIcon,
  UserGroupIcon,
  MapPinIcon,
  HeartIcon,
  CheckCircleIcon,
  PencilSquareIcon,
  HomeIcon,
  PauseIcon,
  ArrowPathIcon,
  RadioIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  CloudIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { MissionStatus, MissionComment } from '../../types';
import { formatMissionStatus } from '../../utils/formatUtils';

// Which mission status each event auto-advances to (null = timeline-only, no status change)
const EVENT_STATUS_MAP: Record<string, MissionStatus | null> = {
  AIRCRAFT_DISPATCHED: MissionStatus.DISPATCHED,
  AIRCRAFT_LAUNCHED: MissionStatus.AIRCRAFT_LAUNCHED,
  ENROUTE_TO_CREW_BASE: null,
  ARRIVED_AT_CREW_BASE: null,
  CREW_ABOARD: null,
  WEATHER_HOLD: null,
  AIRCRAFT_STAGED: null,
  ARRIVED_AT_SCENE: MissionStatus.ON_SCENE,
  PATIENT_CONTACT: null,
  NO_TRANSPORT: null,
  TRANSPORTING_PATIENT: MissionStatus.TRANSPORTING,
  ARRIVED_AT_DESTINATION: MissionStatus.AT_DESTINATION,
  PATIENT_HANDOFF: null,
  DIVERTED: null,
  MISSION_COMPLETE: MissionStatus.MISSION_COMPLETE,
  STOOD_DOWN: MissionStatus.CANCELLED,
  RETURNING_TO_BASE: null,
  AIRCRAFT_HOME: null,
};

interface MissionEvent {
  id: string;
  label: string;
  description: string;
  icon: React.ForwardRefExoticComponent<any>;
  color: string;
  advancesStatus: boolean; // true = moves the stepper
  style?: 'primary' | 'kpi' | 'safety' | 'default'; // visual treatment
}

const MISSION_EVENTS: MissionEvent[] = [
  // --- Stepper-advancing events (primary) ---
  {
    id: 'AIRCRAFT_DISPATCHED',
    label: 'Aircraft Dispatched',
    description: 'Aircraft unit dispatched',
    icon: RadioIcon,
    color: 'blue',
    advancesStatus: true,
    style: 'primary',
  },
  {
    id: 'AIRCRAFT_LAUNCHED',
    label: 'Aircraft Airborne',
    description: 'Aircraft departed from base',
    icon: RocketLaunchIcon,
    color: 'purple',
    advancesStatus: true,
    style: 'primary',
  },
  // --- Sub-events (timeline only) ---
  {
    id: 'ENROUTE_TO_CREW_BASE',
    label: 'Enroute to Crew Base',
    description: 'Heading to pick up crew',
    icon: TruckIcon,
    color: 'slate',
    advancesStatus: false,
  },
  {
    id: 'ARRIVED_AT_CREW_BASE',
    label: 'At Crew Base',
    description: 'Aircraft at crew pickup location',
    icon: MapPinIcon,
    color: 'slate',
    advancesStatus: false,
  },
  {
    id: 'CREW_ABOARD',
    label: 'Crew Aboard',
    description: 'Medical crew on aircraft',
    icon: UserGroupIcon,
    color: 'slate',
    advancesStatus: false,
  },
  // --- Safety events ---
  {
    id: 'WEATHER_HOLD',
    label: 'Weather Hold',
    description: 'Holding due to weather conditions',
    icon: CloudIcon,
    color: 'amber',
    advancesStatus: false,
    style: 'safety',
  },
  {
    id: 'AIRCRAFT_STAGED',
    label: 'Staged / Holding',
    description: 'Holding for operational reasons',
    icon: PauseIcon,
    color: 'amber',
    advancesStatus: false,
    style: 'safety',
  },
  // --- Stepper-advancing ---
  {
    id: 'ARRIVED_AT_SCENE',
    label: 'Arrived at Scene',
    description: 'Aircraft landed at scene',
    icon: MapPinIcon,
    color: 'green',
    advancesStatus: true,
    style: 'primary',
  },
  // --- KPI event (elevated, distinct styling) ---
  {
    id: 'PATIENT_CONTACT',
    label: 'Patient Contact',
    description: 'Medical team with patient — KPI milestone',
    icon: HeartIcon,
    color: 'pink',
    advancesStatus: false,
    style: 'kpi',
  },
  {
    id: 'NO_TRANSPORT',
    label: 'No Transport',
    description: 'Patient treated on scene, ground transport',
    icon: NoSymbolIcon,
    color: 'amber',
    advancesStatus: false,
    style: 'safety',
  },
  {
    id: 'TRANSPORTING_PATIENT',
    label: 'Transporting Patient',
    description: 'Enroute to hospital with patient',
    icon: TruckIcon,
    color: 'orange',
    advancesStatus: true,
    style: 'primary',
  },
  {
    id: 'DIVERTED',
    label: 'Diverted',
    description: 'Diverted to alternate destination',
    icon: ExclamationTriangleIcon,
    color: 'amber',
    advancesStatus: false,
    style: 'safety',
  },
  {
    id: 'ARRIVED_AT_DESTINATION',
    label: 'At Hospital',
    description: 'Arrived at receiving facility',
    icon: MapPinIcon,
    color: 'red',
    advancesStatus: true,
    style: 'primary',
  },
  {
    id: 'PATIENT_HANDOFF',
    label: 'Patient Handed Off',
    description: 'Patient transferred to facility',
    icon: HeartIcon,
    color: 'slate',
    advancesStatus: false,
  },
  {
    id: 'MISSION_COMPLETE',
    label: 'Mission Complete',
    description: 'Mission finished',
    icon: CheckCircleIcon,
    color: 'emerald',
    advancesStatus: true,
    style: 'primary',
  },
  // --- Abort / Stand Down ---
  {
    id: 'STOOD_DOWN',
    label: 'Stood Down / Cancelled',
    description: 'Mission aborted or stood down',
    icon: XCircleIcon,
    color: 'red',
    advancesStatus: true,
    style: 'safety',
  },
  // --- Post-mission sub-events ---
  {
    id: 'RETURNING_TO_BASE',
    label: 'Returning to Base',
    description: 'Heading back to home base',
    icon: ArrowPathIcon,
    color: 'slate',
    advancesStatus: false,
  },
  {
    id: 'AIRCRAFT_HOME',
    label: 'Aircraft Home',
    description: 'Aircraft back at home base',
    icon: HomeIcon,
    color: 'slate',
    advancesStatus: false,
  },
];

// Smart next-actions: given current status, what are the 2-3 most likely next events?
// Operational flags that modify next-actions independent of mission status
interface OpState {
  noTransport: boolean;
  stoodDown: boolean;
  returning: boolean;
  aircraftHome: boolean;
  weatherHold: boolean;
  handedOff: boolean;
}

function getNextActions(status: MissionStatus, ops: OpState): string[] {
  // Aircraft is home — only Mission Complete left
  if (ops.aircraftHome) return ['MISSION_COMPLETE'];

  // Returning to base — wait for home or complete
  if (ops.returning) return ['AIRCRAFT_HOME', 'MISSION_COMPLETE'];

  // Stood down — get the aircraft back
  if (ops.stoodDown) return ['RETURNING_TO_BASE', 'MISSION_COMPLETE'];

  // Weather hold — resume depends on what phase we're in
  if (ops.weatherHold) {
    const preTransport = [MissionStatus.DISPATCHED, MissionStatus.CREW_NOTIFIED, MissionStatus.AIRCRAFT_NOTIFIED,
      MissionStatus.AIRCRAFT_LAUNCHED, MissionStatus.AIRCRAFT_AT_CREW_BASE, MissionStatus.CREW_ABOARD,
      MissionStatus.ENROUTE_TO_SCENE].includes(status);
    if (preTransport) return ['AIRCRAFT_LAUNCHED', 'STOOD_DOWN'];
    // During transport — resume means continue to destination
    return ['ARRIVED_AT_DESTINATION', 'DIVERTED', 'STOOD_DOWN'];
  }

  // No transport — patient going by ground, get aircraft back
  if (ops.noTransport) return ['RETURNING_TO_BASE', 'MISSION_COMPLETE'];

  // Patient handed off at hospital — wrap up
  if (ops.handedOff) return ['RETURNING_TO_BASE', 'MISSION_COMPLETE'];

  // Normal status-based flow
  const map: Record<string, string[]> = {
    [MissionStatus.RECEIVED]: ['AIRCRAFT_DISPATCHED', 'STOOD_DOWN'],
    [MissionStatus.ASSIGNED]: ['AIRCRAFT_DISPATCHED', 'STOOD_DOWN'],
    [MissionStatus.DISPATCHED]: ['AIRCRAFT_LAUNCHED', 'WEATHER_HOLD', 'STOOD_DOWN'],
    [MissionStatus.CREW_NOTIFIED]: ['AIRCRAFT_LAUNCHED', 'WEATHER_HOLD', 'STOOD_DOWN'],
    [MissionStatus.AIRCRAFT_NOTIFIED]: ['AIRCRAFT_LAUNCHED', 'WEATHER_HOLD', 'STOOD_DOWN'],
    [MissionStatus.AIRCRAFT_LAUNCHED]: ['ARRIVED_AT_SCENE', 'WEATHER_HOLD', 'STOOD_DOWN'],
    [MissionStatus.AIRCRAFT_AT_CREW_BASE]: ['ARRIVED_AT_SCENE', 'WEATHER_HOLD', 'STOOD_DOWN'],
    [MissionStatus.CREW_ABOARD]: ['ARRIVED_AT_SCENE', 'WEATHER_HOLD', 'STOOD_DOWN'],
    [MissionStatus.ENROUTE_TO_SCENE]: ['ARRIVED_AT_SCENE', 'WEATHER_HOLD', 'DIVERTED', 'STOOD_DOWN'],
    [MissionStatus.ON_SCENE]: ['PATIENT_CONTACT', 'TRANSPORTING_PATIENT', 'NO_TRANSPORT'],
    [MissionStatus.PATIENT_CONTACT]: ['TRANSPORTING_PATIENT', 'NO_TRANSPORT'],
    [MissionStatus.TRANSPORTING]: ['ARRIVED_AT_DESTINATION', 'DIVERTED', 'WEATHER_HOLD'],
    [MissionStatus.AT_DESTINATION]: ['PATIENT_HANDOFF', 'RETURNING_TO_BASE'],
    [MissionStatus.MISSION_COMPLETE]: ['RETURNING_TO_BASE', 'AIRCRAFT_HOME'],
  };
  return map[status] || ['AIRCRAFT_DISPATCHED'];
}

interface StatusUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentStatus: MissionStatus;
  onStatusUpdate: (status: MissionStatus, notes?: string) => void;
  onAircraftEvent?: (event: string, notes?: string) => void;
  missionComments?: MissionComment[];
}

const StatusUpdateModal: React.FC<StatusUpdateModalProps> = ({
  isOpen,
  onClose,
  currentStatus,
  onStatusUpdate,
  onAircraftEvent,
  missionComments = [],
}) => {
  const [showOther, setShowOther] = useState(false);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [customNotes, setCustomNotes] = useState('');
  const [lastClicked, setLastClicked] = useState<string | null>(null);
  const [ops, setOps] = useState<OpState>({
    noTransport: false,
    stoodDown: false,
    returning: false,
    aircraftHome: false,
    weatherHold: false,
    handedOff: false,
  });

  if (!isOpen) return null;

  const nextActionIds = getNextActions(currentStatus, ops);
  const nextActions = MISSION_EVENTS.filter(e => nextActionIds.includes(e.id));
  const otherEvents = MISSION_EVENTS.filter(e => !nextActionIds.includes(e.id));

  const handleEventClick = (event: MissionEvent) => {
    const timestamp = format(new Date(), 'HH:mm:ss');
    const newStatus = EVENT_STATUS_MAP[event.id];

    // Flash feedback
    setLastClicked(event.id);
    setTimeout(() => setLastClicked(null), 1500);

    if (newStatus && newStatus !== currentStatus) {
      const notes = `${event.label} at ${timestamp}`;
      onStatusUpdate(newStatus, notes);
    } else {
      if (onAircraftEvent) {
        const notes = `${event.label} at ${timestamp}`;
        onAircraftEvent(event.id, notes);
      }
    }

    // Update operational flags based on what was clicked
    setOps(prev => {
      const next = { ...prev };
      switch (event.id) {
        case 'NO_TRANSPORT': next.noTransport = true; break;
        case 'STOOD_DOWN': next.stoodDown = true; break;
        case 'RETURNING_TO_BASE': next.returning = true; next.noTransport = false; next.stoodDown = false; next.handedOff = false; break;
        case 'AIRCRAFT_HOME': next.aircraftHome = true; next.returning = false; break;
        case 'WEATHER_HOLD': next.weatherHold = true; break;
        case 'AIRCRAFT_LAUNCHED': next.weatherHold = false; break;
        case 'ARRIVED_AT_DESTINATION': next.weatherHold = false; break;
        case 'ARRIVED_AT_SCENE': next.weatherHold = false; break;
        case 'PATIENT_HANDOFF': next.handedOff = true; break;
        case 'MISSION_COMPLETE': next.returning = false; next.aircraftHome = false; break;
      }
      return next;
    });
  };

  const handleOtherSubmit = () => {
    if (customNotes.trim()) {
      const notes = `Manual: ${customNotes} at ${format(new Date(), 'HH:mm')}`;
      if (onAircraftEvent) {
        onAircraftEvent('CUSTOM', notes);
      }
      setShowOther(false);
      setCustomNotes('');
    }
  };

  const getColorClasses = (event: MissionEvent) => {
    const { color, style } = event;

    // KPI milestone — elevated pink/rose styling
    if (style === 'kpi') {
      return 'bg-pink-50 hover:bg-pink-100 text-pink-700 border-pink-300 border-2';
    }
    // Safety events — amber/warning styling with dashed border
    if (style === 'safety') {
      return 'bg-amber-50 hover:bg-amber-100 text-amber-800 border-amber-400 border-dashed';
    }
    // Default sub-events (timeline only)
    if (!event.advancesStatus) {
      return 'bg-gray-50 hover:bg-gray-100 text-gray-600 border-gray-200';
    }
    // Primary stepper-advancing events
    const colors: Record<string, string> = {
      blue: 'bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300',
      purple: 'bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-300',
      green: 'bg-green-50 hover:bg-green-100 text-green-700 border-green-300',
      orange: 'bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-300',
      red: 'bg-red-50 hover:bg-red-100 text-red-700 border-red-300',
      emerald: 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-300',
    };
    return colors[color] || 'bg-gray-50 hover:bg-gray-100 text-gray-600 border-gray-200';
  };

  // Recent event log from comments
  const recentEvents = missionComments
    .filter(c =>
      c.commentText.includes('at ') &&
      (c.commentText.match(/^\w/) || c.commentText.startsWith('Manual:'))
    )
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 10);

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Mission Events</h3>
            <p className="text-sm text-gray-500">
              Current: <span className="font-medium">{formatMissionStatus(currentStatus)}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Event buttons */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-280px)]">
          {!showOther ? (
            <>
              {/* Next Actions — prominent */}
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Next Actions</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                {nextActions.map((event) => {
                  const Icon = event.icon;
                  const wasJustClicked = lastClicked === event.id;
                  return (
                    <button
                      key={event.id}
                      onClick={() => handleEventClick(event)}
                      className={`
                        p-4 rounded-lg border-2 transition-all duration-200 text-left hover:shadow-md
                        ${getColorClasses(event)}
                        ${wasJustClicked ? 'ring-2 ring-green-500 ring-offset-1' : ''}
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-6 w-6 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-sm">{event.label}</h4>
                          <p className="text-xs opacity-70">{event.description}</p>
                          {wasJustClicked && <p className="text-xs font-semibold text-green-600 mt-0.5">Logged</p>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* All Events — collapsed by default */}
              <button
                onClick={() => setShowAllEvents(!showAllEvents)}
                className="text-xs text-gray-500 hover:text-gray-700 mb-3 flex items-center gap-1"
              >
                <span className={`transition-transform ${showAllEvents ? 'rotate-90' : ''}`}>▸</span>
                {showAllEvents ? 'Hide' : 'Show'} all events ({otherEvents.length})
              </button>

              {showAllEvents && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 mb-4">
                  {otherEvents.map((event) => {
                    const Icon = event.icon;
                    const wasJustClicked = lastClicked === event.id;
                    return (
                      <button
                        key={event.id}
                        onClick={() => handleEventClick(event)}
                        className={`
                          p-2.5 rounded-lg border transition-all duration-200 text-left
                          ${getColorClasses(event)}
                          ${wasJustClicked ? 'ring-2 ring-green-500 ring-offset-1' : ''}
                        `}
                      >
                        <div className="flex items-start gap-2">
                          <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <h4 className="text-xs font-medium leading-tight">{event.label}</h4>
                            {wasJustClicked && <p className="text-[10px] font-semibold text-green-600">Logged</p>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="text-center">
                <button
                  onClick={() => setShowOther(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <PencilSquareIcon className="h-4 w-4 mr-2" />
                  Manual Entry
                </button>
              </div>
            </>
          ) : (
            <div className="max-w-lg mx-auto">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Add Custom Note</h4>
              <textarea
                value={customNotes}
                onChange={(e) => setCustomNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder="Enter event details or notes..."
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-2">
                <ClockIcon className="h-3 w-3 inline mr-1" />
                Timestamp added automatically
              </p>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={handleOtherSubmit}
                  disabled={!customNotes.trim()}
                  className="flex-1 btn btn-primary disabled:opacity-50"
                >
                  Add Entry
                </button>
                <button
                  onClick={() => { setShowOther(false); setCustomNotes(''); }}
                  className="flex-1 btn btn-secondary"
                >
                  Back
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Recent log */}
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 max-h-40 overflow-y-auto">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Recent Events</h4>
          {recentEvents.length > 0 ? (
            recentEvents.map((comment) => (
              <div key={comment.id} className="text-xs text-gray-600 py-0.5">
                <span className="font-medium">{format(new Date(comment.timestamp), 'HH:mm:ss')}</span>
                <span className="text-gray-400 mx-1">—</span>
                <span>{comment.commentText}</span>
              </div>
            ))
          ) : (
            <p className="text-xs text-gray-400 italic">No events logged yet</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-white border-t border-gray-200 flex justify-end">
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusUpdateModal;
